import { CalendarDays, Clock, MapPin, ShieldCheck, Users, AlertTriangle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { usePageTitle } from "@/lib/api";
import { FAQS, BRAND, TRYOUT, directionsUrl } from "@/lib/content";
import { PageHero, SectionHeading, MapEmbed, Reveal } from "@/components/site/Bits";
import { Countdown } from "@/components/site/Countdown";
import { RegisterButtons } from "@/components/site/RegisterButtons";

export const FaqList = ({ prefix = "faq" }) => (
  <Accordion type="single" collapsible className="border-t border-line" data-testid={`${prefix}-accordion`}>
    {FAQS.map((f, i) => (
      <AccordionItem key={f.q} value={`f${i}`} className="border-line">
        <AccordionTrigger className="py-5 text-left font-display text-lg sm:text-xl font-extrabold uppercase italic hover:no-underline hover:text-gold" data-testid={`${prefix}-question-${i}`}>
          {f.q}
        </AccordionTrigger>
        <AccordionContent className="text-sm sm:text-base text-slate-300 pb-5">{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

const details = [
  [CalendarDays, "Date", TRYOUT.date],
  [Clock, "Check-In", TRYOUT.checkin],
  [Clock, "Tryouts", TRYOUT.window],
  [MapPin, "Location", `${TRYOUT.location} • ${TRYOUT.address}`],
  [Users, "Ages", TRYOUT.ages],
  [ShieldCheck, "Equipment", TRYOUT.equipment],
];

export default function Tryouts() {
  usePageTitle("Tryouts & Combine");
  const { settings } = useSite();
  return (
    <div data-testid="tryouts-page">
      <PageHero testid="tryouts-hero" eyebrow="Tryouts & Combine" image={BRAND.gloves}
        title={<>2027 Tri County<br /><span className="text-gold">Allstars Tryouts</span></>}>
        <div className="mt-10">
          <p className="eyebrow mb-4">Countdown to tryout day</p>
          <Countdown target={settings.tryout_datetime || "2026-10-25T10:00:00-04:00"} testid="tryout-countdown" />
        </div>
      </PageHero>

      <section className="bg-pitch py-16 sm:py-24">
        <div className="wrap grid gap-12 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <SectionHeading eyebrow="Event Details" title="Tryout Day" />
            <dl className="mt-10 grid sm:grid-cols-2 gap-px bg-line/60" data-testid="tryout-details">
              {details.map(([Icon, k, v]) => (
                <div key={k} className="bg-pitch p-5">
                  <dt className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold"><Icon className="h-3.5 w-3.5" />{k}</dt>
                  <dd className="mt-2 font-display text-xl font-extrabold uppercase italic">{v}</dd>
                </div>
              ))}
            </dl>
            <a href={directionsUrl(TRYOUT.address)} target="_blank" rel="noreferrer" className="mt-6 inline-flex text-sm font-semibold uppercase tracking-wider text-gold hover:text-white" data-testid="tryout-directions-link">Get directions to PSLAA Field →</a>
          </div>
          <Reveal>
            <div className="card-navy p-6 sm:p-8 border-t-4 !border-t-gold">
              <p className="eyebrow mb-3">Register Now</p>
              <h3 className="h-display text-4xl">Lock in your athlete's tryout.</h3>
              <p className="mt-3 text-sm text-slate-300">Complete the official player registration and secure your spot for tryout day.</p>
              <div className="mt-6"><RegisterButtons /></div>
              <div className="mt-6 flex gap-3 bg-pitch/60 border border-line p-4" data-testid="invitation-notice">
                <AlertTriangle className="h-5 w-5 text-gold shrink-0 mt-0.5" />
                <p className="text-sm text-slate-300"><strong className="text-white">Receiving an invitation does not guarantee a roster spot.</strong> All athletes must participate in the selection process.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy py-16 sm:py-24">
        <div className="wrap grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading eyebrow="FAQ" title="Tryout Questions" sub="Everything families need to know before tryout day." />
          <FaqList prefix="tryout-faq" />
        </div>
      </section>

      <section className="bg-pitch">
        <MapEmbed className="h-80 sm:h-96" />
      </section>
    </div>
  );
}
